// [PURPOSE]: Curated enum of triggerId values for the `triggeredEffect` effect type.
// [WHY]: Spec C (2026-05-16) introduces the 7th generic effect type — a data-declared
//   bundle { triggerId, condition?, payload[] } that fires automatically when a named
//   game event occurs (attack hits, save is rolled, damage is taken, rest completes).
//   Rule 11 compliance: trigger keys are enum values, not free strings.
// [INTEGRATION]: effectCalculator.fireTriggeredEffects(triggerId, ...) filters by
//   triggerId === one of these enum values. CW EffectBuilder dropdown picks from
//   this list. Each entry's `allowedFilterKeys` array tells the Author UI which
//   trigger-specific filter keys are valid for the picked trigger (e.g., on_hit
//   admits ammoTypeMatch but on_rest_completed doesn't).
// [SPEC]: docs/superpowers/specs/2026-05-16-triggered-effect-design.md
// [PLAN]: docs/superpowers/plans/2026-05-16-triggered-effect.md Task 1

/**
 * The five v1 trigger IDs. Recommended set per the design doc's defense:
 *   - on_hit / on_crit close the 5 inert magical-ammo fields + Flame Tongue +
 *     Frost Brand + Vicious Weapon SRD items.
 *   - on_save_rolled closes Mind Sharpener.
 *   - on_take_damage infra ships with a BREADCRUMB (no SRD consumer in v1; v1.x
 *     adds Hellish Rebuke / Champion Survivor regen / Stoneskin tick).
 *   - on_rest_completed closes Spell-Refueling Ring.
 *
 * Future (deferred to v1.x): on_turn_start / on_turn_end (needs combat tracker),
 * on_enter_zone (needs map state), on_initiative_rolled, on_attack_missed.
 *
 * Each entry's `allowedFilterKeys` documents which keys the trigger's `condition`
 * object can carry. Keys are matched against the firer's `context` object at
 * fire-time. `when` is the universal flag-key that uses existing CONDITION_FLAGS
 * vocabulary (effectCalculator._buildContext).
 */
export const TRIGGER_VOCABULARY = Object.freeze([
  {
    value: 'on_hit',
    label: 'On Hit',
    description: 'Fires when an attack roll resolves as a hit. SRD: +1 arrow damage rider, Flame Tongue +2d6 fire on hit, Arrow of Slaying creature-type rider.',
    allowedFilterKeys: Object.freeze(['ammoTypeMatch', 'targetTypeMatch', 'damageTypeMatch', 'when']),
  },
  {
    value: 'on_crit',
    label: 'On Crit',
    description: 'Fires when an attack roll resolves as a critical hit (isCrit === true). SRD: Vicious Weapon +7 on crit, Hunter Giant Killer crit-doublings.',
    allowedFilterKeys: Object.freeze(['ammoTypeMatch', 'targetTypeMatch', 'damageTypeMatch', 'when']),
  },
  {
    value: 'on_save_rolled',
    label: 'On Save Rolled',
    description: 'Fires when a saving throw is rolled (any outcome). SRD: Mind Sharpener advantage on concentration save, Indomitable rerolls.',
    allowedFilterKeys: Object.freeze(['ability', 'when']),
  },
  {
    value: 'on_take_damage',
    label: 'On Take Damage',
    description: 'Fires when HP decrements via damage. v1.x consumers: Hellish Rebuke auto-retaliation, Champion Survivor regen, Stoneskin tick consumption. See COMING_SOON_ROADMAP §3.21.',
    allowedFilterKeys: Object.freeze(['damageTypeMatch', 'when']),
  },
  {
    value: 'on_rest_completed',
    label: 'On Rest Completed',
    description: 'Fires when a short or long rest completes. SRD: Spell-Refueling Ring "regain 1 slot at dawn".',
    allowedFilterKeys: Object.freeze(['restType', 'when']),
  },
  // [SPEC D TASK 21 — 2026-05-17]: Combat-tracker triggers. Spec C deferred
  //   these because no combat tracker existed; Spec D's CombatTab + advanceTurn
  //   firer wire-up enables them.
  {
    value: 'on_turn_start',
    label: 'On Turn Start',
    description: 'Fires at the start of a combatant\'s turn. SRD: Champion Survivor regen (heal when ≤ half HP at turn start).',
    allowedFilterKeys: Object.freeze(['combatantKind', 'hpThreshold', 'when']),
  },
  {
    value: 'on_turn_end',
    label: 'On Turn End',
    description: 'Fires at the end of a combatant\'s turn. SRD: Concentration save prompts; condition tick-down (e.g., Frightened ends on save).',
    allowedFilterKeys: Object.freeze(['combatantKind', 'when']),
  },
]);

/**
 * @param {string} triggerId
 * @returns {object | undefined}
 */
export function getTriggerById(triggerId) {
  if (!triggerId) return undefined;
  return TRIGGER_VOCABULARY.find((t) => t.value === triggerId);
}

/**
 * @param {string} triggerId
 * @returns {boolean}
 */
export function isValidTriggerId(triggerId) {
  if (!triggerId) return false;
  return TRIGGER_VOCABULARY.some((t) => t.value === triggerId);
}

/**
 * Default empty value shape for a new `triggeredEffect` row in the CW
 * EffectBuilder. Used by EffectBuilderControls when the user picks the type
 * from the dropdown.
 */
export function emptyTriggeredEffectShape() {
  return {
    triggerId: 'on_hit',
    condition: {},
    payload: [],
    onceThenConsume: false,
    sourceLabel: '',
  };
}
